import { InternalEntry, InternalOverflowEntry } from './types';

/**
 * Renders the tooltip content for the center label. This includes displaying
 * each individual entry as well as any entries within an overflow entry.
 */
export function CenterTooltipContent(props: {
  entries: (InternalOverflowEntry | InternalEntry)[];
  formatPercent: (value: number) => string;
  formatValue?: (value: number) => string;
}) {
  const flatEntries = props.entries.flatMap((entry) =>
    'entries' in entry ? entry.entries : entry
  );
  return (
    <div className="grid max-h-[200px] grid-cols-[1fr_auto_auto] gap-x-2 overflow-y-auto">
      {flatEntries
        .filter(({ value }) => value > 0)
        .map((entry) => (
          <Entry
            key={entry.label}
            entry={entry}
            formatPercent={props.formatPercent}
            formatValue={props.formatValue}
          />
        ))}
    </div>
  );
}

/**
 * Renders the tooltip content for an overflow entry. This includes displaying
 * the overflow label as a header and listing each of the grouped entries.
 */
export function OverflowTooltipContent(props: {
  entry: InternalOverflowEntry;
  formatPercent: (value: number) => string;
  formatValue?: (value: number) => string;
}) {
  return (
    <div className="flex max-h-[200px] flex-col overflow-hidden">
      <div className="flex items-center gap-2 type-body2">
        <div className="h-2 w-2" style={{ backgroundColor: props.entry.color }} />
        <strong>{props.entry.label}</strong>
      </div>
      <div className="grid grid-cols-[1fr_auto_auto] gap-x-2 overflow-auto">
        {props.entry.entries
          .filter(({ value }) => value > 0)
          .map((entry) => (
            <Entry
              key={entry.label}
              entry={entry}
              formatPercent={props.formatPercent}
              formatValue={props.formatValue}
            />
          ))}
      </div>
    </div>
  );
}

/**
 * Renders the tooltip content for an individual entry, displaying the label,
 * (formatted) value, and percentage.
 */
export function EntryTooltipContent(props: {
  entry: InternalEntry;
  formatPercent: (value: number) => string;
  formatValue?: (value: number) => string;
}) {
  return (
    // 'contents' allows the parent component to align columns for multiple entries
    <div className="grid grid-cols-[1fr_auto_auto] gap-x-2">
      <Entry
        entry={props.entry}
        formatPercent={props.formatPercent}
        formatValue={props.formatValue}
      />
    </div>
  );
}

function Entry(props: {
  entry: InternalEntry;
  formatPercent: (value: number) => string;
  formatValue?: (value: number) => string;
}) {
  return (
    <>
      <div className="flex items-center gap-2">
        <div className="h-2 w-2" style={{ backgroundColor: props.entry.color }} />
        <div>{props.entry.label}</div>
      </div>
      <div className="flex min-w-4 flex-row justify-end">
        {props.formatValue?.(props.entry.value) ?? props.entry.value}
      </div>
      <div className="flex min-w-6 flex-row justify-end">
        {props.formatPercent(props.entry.value)}
      </div>
    </>
  );
}
